import React, { useState, useEffect } from "react";
import MasterTable from "../common/MasterTable";
import CustomAlert from "../../helpers/CustomAlert";
import {
  axiosPostApi,
  axiosDownloadFile,
} from "../../helpers/interceptorServices";
import { apiList } from "../../helpers/apiList";
import Loader from "../Loader/Loader";
import { format, parse } from "date-fns";
import { useSelector } from "react-redux";
import HeaderWithBreadcrumb from "../common/HeaderWithBreadcrumb";
import ReportOrderFilterModal from "./ReportOrderFilterModal";
import FilterBar from "../common/FilterBar";
import { asStatusList } from "../common/filterOptions";

const REPORT_STATUS_OPTIONS = [
  { value: "PENDING", label: "Pending" },
  { value: "PROCESSING", label: "Processing" },
  { value: "COMPLETED", label: "Completed" },
  { value: "FAILED", label: "Failed" },
];

const defaultFilterData = {
  retailerId: "",
  consigneeName: "",
  consigneeMobile: "",
  fromDate: "",
  toDate: "",
  status: [],
};

const ReportOrderMaster = () => {
  const pageSize = 10;
  const { user } = useSelector((state) => state.auth);

  const [noData, setNoData] = useState(false);
  const [editData, setEditData] = useState({});
  const [orderData, setOrderData] = useState([]);
  const [modalAction, setModalAction] = useState("");
  const [loading, setLoading] = useState(false);
  const [pageDetails, setPageDetails] = useState(null);
  const loggedInUserName = sessionStorage.getItem("username");
  const [currentPage, setCurrentPage] = useState(1);
  const [filterData, setFilterData] = useState(defaultFilterData);
  const [reportRange, setReportRange] = useState({ fromDate: "", toDate: "" });
  const [columnData, setColumnData] = useState("");
  const [rowData, setRowData] = useState(null);
  const [showProductDetail, setShowProductDetail] = useState(false);
  const [productDetail, setProductDetail] = useState([]);
  const [isModalOpenCreate, setIsModalOpenCreate] = useState(false);

  const [isCancelShipmentModalOpen, setIsCancelShipmentModalOpen] =
    useState(false);

  const handleReset = () => {
    setFilterData(defaultFilterData);
    fetchOrderData(1);
  };

  const [isModalOpenFilter, setIsModalOpenFilter] = useState(false);
  const openModalFilter = () => {
    setIsModalOpenFilter(true);
  };

  const closeModalFilter = () => {
    setIsModalOpenFilter(false);
  };

  useEffect(() => {
    fetchOrderData(1, filterData);
  }, []);

  // `silent` refreshes the table in place (used by the auto-refresh below)
  const fetchOrderData = async (
    currentPage,
    filters = filterData,
    silent = false,
  ) => {
    if (!silent) setLoading(true);

    try {
      const requestBody = {
        username: user?.data?.username,
        // the list endpoint reads fromDate / toDate (reqFromDate was ignored)
        fromDate: filters.fromDate ? formatDate(filters.fromDate) : "",
        toDate: filters.toDate ? formatDate(filters.toDate) : "",
        status: filters.status?.length ? filters.status : [],
      };

      const response = await axiosPostApi(
        `${apiList.ORDER_MASTER_DOWNLOAD_HISTORY_LIST}?pageSize=${pageSize}&pageNo=${currentPage}`,
        requestBody
      );
      if (
        response?.data?.status === true &&
        response?.data?.respCode === "00"
      ) {
        setPageDetails(response?.data?.pageDetails);
        const modifiedData = modifyStructure(response?.data?.data);
        setOrderData(modifiedData);
        setNoData(modifiedData?.length === 0);
      } else {
        if (silent) return;
        setNoData(true);
        setOrderData([]);
        // "01" is just "no reports match": the empty table says so
        if (response?.data?.respCode === "01") return;
        CustomAlert({
          title: "Failed",
          text: response?.data?.message || "Failed to fetch data",
          icon: "error",
          confirmButtonText: "Retry",
        });
      }
    } catch (error) {
      if (silent) return;
      setNoData(true);
      setOrderData([]);
      CustomAlert({
        title: "Error",
        text: "An error occurred while fetching the data. Please try again later.",
        icon: "error",
        confirmButtonText: "Retry",
      });
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Reports are generated in the background; while any is still Pending or
  // PROCESSING, re-check every 15s so Download appears once it is COMPLETED.
  const hasReportInProgress = orderData.some((row) =>
    ["PENDING", "PROCESSING"].includes(row?.status?.toUpperCase()),
  );

  useEffect(() => {
    if (!hasReportInProgress) return undefined;
    const timer = setInterval(() => {
      fetchOrderData(currentPage, filterData, true);
    }, 15000);
    return () => clearInterval(timer);
  }, [hasReportInProgress, currentPage, filterData]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split("-");
    return `${day}-${month}-${year}`;
  };

  const handleReportDownload = async (row) => {
    try {
      await axiosDownloadFile(`/master/order/download/${row.id}`, row.fileName);
    } catch (error) {
      console.error("Order report download failed:", error);
      CustomAlert({
        title: "Error",
        text: "Unable to download the report. Please try again later.",
        icon: "error",
      });
    }
  };

  const modifyStructure = (response) => {
    const modified = response?.map((user, index) => {
      const formattedCreDate = user?.createdDate
        ? parse(user.createdDate, "dd-MM-yyyy HH:mm:ss", new Date())
        : null;

      return {
        ...user,
        id: user.id || index + 1,
        createdDate: formattedCreDate
          ? [
              format(formattedCreDate, "dd-MM-yyyy"),
              <br key={index} />,
              format(formattedCreDate, "HH:mm:ss"),
            ]
          : user.createdDate || "",
        downloadUrl:
          user.status?.toUpperCase() === "COMPLETED" ? (
            <span
              key={`dl-${index}`}
              className="text-[#14b8a6] cursor-pointer underline"
              onClick={() => handleReportDownload(user)}
            >
              Download
            </span>
          ) : (
            "-"
          ),
      };
    });
    return modified;
  };

  const SMSDownloadHeaders = [
    "Request ID",
    "Req Date & Time",
    "From Date",
    "To Date",
    "Status",
    "Remark",
    "File",
  ];

  const SMSDownloadHeaderToKeyMap = {
    "Request ID": "id",
    "Req Date & Time": "createdDate",
    "From Date": "reqFromDate",
    "To Date": "reqToDate",
    Status: "status",
    Remark: "errorMessage",
    File: "downloadUrl",
  };

  const handleAuthorization = (data) => {
    console.log("::data::", data);
  };

  const handleCustomer = (data, actionType = "view") => {
    setModalAction(actionType);
    setColumnData(data);
    if (actionType === "add") {
      setEditData({});
      setIsModalOpenCreate(true); // By OMKAR
    } else if (actionType === "cancelShipment") {
      // By OMKAR
      setIsCancelShipmentModalOpen(true); // By OMKAR
    } else if (actionType === "auth") {
      let custData = orderData.find((item) => item?.id === data?.id);
      if (custData) {
        setEditData(custData);
        setIsModalOpenCreate(true);
      }
    } else {
      let custData = orderData.find((item) => item?.id === data?.id);
      if (custData) {
        setEditData(custData);
        setIsModalOpenCreate(true);
      }
    }
  };

  const openModalCreate = () => {
    setIsModalOpenCreate(true);
  };

  const pageSizeCount = pageDetails === null ? 0 : pageDetails.pageSize;
  const totalRecord = pageDetails === null ? 0 : pageDetails.totalRecords;

  const handleProductDetails = (data) => {
    if (data?.products && data?.products.length > 0) {
      setProductDetail(data?.products);
    } else {
      setProductDetail([]);
    }
    setShowProductDetail(true);
  };

  return (
    <>
      <HeaderWithBreadcrumb
        title="Report Order Master"
        breadcrumb="Master"
        actions={
          <button
            type="button"
            onClick={openModalFilter}
            className="ml-3 rounded-lg bg-[#FF702B] px-3 py-1 text-sm text-white"
          >
            Request report
          </button>
        }
      />
      <FilterBar
        values={filterData}
        onChange={(next) => {
          setFilterData(next);
          setCurrentPage(1);
          fetchOrderData(1, next);
        }}
        selects={[{ key: "status", allLabel: "All statuses", chipLabel: "Status", options: REPORT_STATUS_OPTIONS, ...asStatusList }]}
        dateRange={{ fromKey: "fromDate", toKey: "toDate", label: "Requested" }}
      />
      <div className="bg-[#FFFFFF] shadow-custom rounded-[14px] px-[10px]">
        {loading ? (
          <Loader />
        ) : (
          <>
            <MasterTable
              loading={loading}
              headers={SMSDownloadHeaders}
              data={orderData}
              handleAction={handleCustomer}
              filterFields={["id"]}
              entityType="Report Order"
              headerToKeyMap={SMSDownloadHeaderToKeyMap}
              handleAuth={handleAuthorization}
              noData={noData}
              pageDetails={pageDetails}
              currentPage={currentPage}
              pageSize={pageSizeCount}
              totalRecord={totalRecord}
              fetchNextData={(page) => fetchOrderData(page, filterData)}
              setCurrentPage={setCurrentPage}
              openModalFilter={openModalFilter}
              handleReset={handleReset}
              filterData={filterData}
              fetchData={() => {}}
              handleProductDetails={handleProductDetails}
              setRowData={setRowData}
              hideAction="hideAction"
              hideFilterControls
            />
          </>
        )}

        {isModalOpenFilter ? (
          <ReportOrderFilterModal
            show={isModalOpenFilter}
            handleClose={closeModalFilter}
            setOrderData={setOrderData}
            setPageDetails={setPageDetails}
            setNoData={setNoData}
            currentPage={currentPage}
            pageSize={pageSize}
            // the report's own period, kept apart from the list filters
            filterData={reportRange}
            setFilterData={setReportRange}
            onRequested={() => {
              setCurrentPage(1);
              fetchOrderData(1, filterData);
            }}
            handleReset={handleReset}
            setCurrentPage={setCurrentPage}
          />
        ) : null}
      </div>
    </>
  );
};

export default ReportOrderMaster;
